import { useMutation } from '@tanstack/react-query'

const SESSION_RESPONSE_CODE = 'ResponseCode'
const SESSION_CODE_VERIFIER = 'CodeVerifier'

export const CIAM_SESSION_TOKEN_STORAGE_KEY = 'SessionToken'

// Session storage value: raw Id token JWT string from `/ciam/token` (used in `store.js` to load `/ciam/user/{userId}` into `userData`).
export const CIAM_ID_TOKEN_STORAGE_KEY = 'CiamIdToken'

export const CIAM_EMAIL_COOKIE = 'email'

// Fired on `window` after `CiamIdToken` is written or removed (same-tab listeners).
export const CIAM_ID_TOKEN_UPDATED_EVENT = 'ciam-id-token-updated'

// Raw access token from session storage (for APIs expecting `SessionToken`, etc.).
export const getCiamSessionToken = () =>
  sessionStorage.getItem(CIAM_SESSION_TOKEN_STORAGE_KEY)

const readCookieValue = name => {
  if (typeof document === 'undefined') {
    return null
  }

  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.trim().split('=')
    if (key === name) {
      return rest.join('=')
    }
  }

  return null
}

const writeSessionCookie = (name, value) => {
  if (typeof document === 'undefined') {
    return
  }

  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : ''
  document.cookie = `${name}=${value}; path=/; SameSite=Lax${secure}`
}

const clearSessionCookie = name => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie = `${name}=; path=/; max-age=0`
}

const clearCiamEmailCookie = () => {
  clearSessionCookie(CIAM_EMAIL_COOKIE)
}

// Writes the email cookie from `/ciam/user` `Email` when a session token is present.
export const syncCiamEmailCookie = email => {
  const sessionToken = getCiamSessionToken()

  if (sessionToken == null || sessionToken === '') {
    clearCiamEmailCookie()
    return
  }

  if (email == null || String(email) === '') {
    clearCiamEmailCookie()
    return
  }

  writeSessionCookie(CIAM_EMAIL_COOKIE, String(email))
}

// Read-only: returns the email cookie only while a session token is present.
export const getCiamEmail = () => {
  const sessionToken = getCiamSessionToken()
  if (sessionToken == null || sessionToken === '') {
    return null
  }

  const email = readCookieValue(CIAM_EMAIL_COOKIE)
  if (email == null || email === '') {
    return null
  }

  return email
}

export const clearCiamSessionAuth = () => {
  sessionStorage.removeItem(CIAM_SESSION_TOKEN_STORAGE_KEY)
  sessionStorage.removeItem(CIAM_ID_TOKEN_STORAGE_KEY)
  clearCiamEmailCookie()

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CIAM_ID_TOKEN_UPDATED_EVENT))
  }
}

export const fetchCiamUserProfile = async (userId, sessionToken) => {
  if (
    typeof userId !== 'string' ||
    userId === '' ||
    typeof sessionToken !== 'string' ||
    sessionToken === ''
  ) {
    return null
  }

  const response = await fetch(
    `${process.env.GATSBY_API_URL}/ciam/user/${encodeURIComponent(userId)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    }
  )

  const contentType = response.headers.get('content-type') ?? ''
  if (!response.ok) {
    return null
  }
  if (contentType.includes('application/json')) {
    return response.json()
  }
  return null
}

const persistCiamTokens = body => {
  if (typeof body !== 'object' || body === null) {
    return
  }

  const accessToken = body.AccessToken ?? body.access_token
  const idToken = body.IdToken ?? body.id_token

  if (accessToken) {
    sessionStorage.setItem(CIAM_SESSION_TOKEN_STORAGE_KEY, accessToken)
  }
  if (idToken) {
    sessionStorage.setItem(CIAM_ID_TOKEN_STORAGE_KEY, String(idToken))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(CIAM_ID_TOKEN_UPDATED_EVENT))
    }
  }

  sessionStorage.removeItem(SESSION_RESPONSE_CODE)
  sessionStorage.removeItem(SESSION_CODE_VERIFIER)
}

const fetchCiamToken = async payload => {
  const response = await fetch(`${process.env.GATSBY_API_URL}/ciam/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  const contentType = response.headers.get('content-type') ?? ''
  let body
  if (contentType.includes('application/json')) {
    body = await response.json()
  } else {
    body = await response.text()
  }

  if (response.ok) {
    persistCiamTokens(body)
    return true
  }
  return false
}

export const captureCiamOAuthCallback = () => {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  if (!code) {
    return
  }

  sessionStorage.setItem(SESSION_RESPONSE_CODE, code)
  url.searchParams.delete('code')
  const qs = url.searchParams.toString()
  const queryPart = qs ? `?${qs}` : ''
  const pathWithQuery = `${url.pathname}${queryPart}${url.hash}`
  window.history.replaceState({}, '', pathWithQuery)

  const tokenPayload = {
    CodeVerifier: sessionStorage.getItem(SESSION_CODE_VERIFIER) ?? '',
    Code: sessionStorage.getItem(SESSION_RESPONSE_CODE) ?? '',
  }

  void (async () => {
    try {
      const ok = await fetchCiamToken(tokenPayload)
      if (ok && getCiamSessionToken()) {
        window.location.href = '/dashboard/'
      }
    } catch (error) {
      console.error('Error calling CIAM /ciam/token:', error)
    }
  })()
}

const fetchCiamAuthorizeResponse = async () => {
  const response = await fetch(
    `${process.env.GATSBY_API_URL}/ciam/authorize-url`,
    {
      method: 'GET',
    }
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  if (!data.Location) {
    throw new Error('Location not found in response')
  }

  return data
}

export const useCiamAuthorize = () => {
  const authorizeMutation = useMutation({
    mutationKey: ['ciam', 'authorize-url'],
    mutationFn: fetchCiamAuthorizeResponse,
    retry: false,
    onSuccess: data => {
      if (data.CodeVerifier != null && data.CodeVerifier !== '') {
        sessionStorage.setItem(SESSION_CODE_VERIFIER, String(data.CodeVerifier))
      }

      const authorizeTarget = new URL(data.Location, window.location.origin)
      window.location.href = authorizeTarget.toString()
    },
    onError: error => {
      console.error('Error fetching authorize URL:', error)
    },
  })

  return {
    handleAuthorize: () => authorizeMutation.mutate(),
    isAuthorizing: authorizeMutation.isPending || authorizeMutation.isSuccess,
  }
}
